/**
 * Affinity (ความสนิทสนม, lane C0-NPC phase N3; design docs/design/C0_NPC_LIFE.html §07): six tiers on a score kept
 * per person beside the memory scores (systems/npcMemory.ts `aff`), raised by help in a fight, quests, gifts they
 * like, meals and fishing together, and lowered by harm in front of them. It never decays by itself (approved
 * default). Data only: the rules are in systems/npcMemory.ts (state, gifts) and systems/npcLife.ts (what each tier
 * changes in the people of the plains).
 */

export interface AffinityTier {
  /** 0 แปลกหน้า … 5 ร่วมเป็นร่วมตาย. */
  readonly n: number;
  readonly name: string;
  /** Score from which the tier holds. */
  readonly at: number;
  /** What it opens, shown with the tier. */
  readonly opens: string;
}

export const AFFINITY_TIERS: readonly AffinityTier[] = [
  { n: 0, name: 'แปลกหน้า', at: 0, opens: 'คุยทั่วไป ซื้อขายราคาปกติ' },
  { n: 1, name: 'รู้จัก', at: 50, opens: 'ทักชื่อ เล่าข่าวลือเล็กๆ' },
  { n: 2, name: 'คุ้นเคย', at: 150, opens: 'ลดราคา 5% บอกของที่ชอบ' },
  { n: 3, name: 'สนิท', at: 350, opens: 'ชวนกินข้าว ตกปลาด้วยกัน ภารกิจส่วนตัว' },
  { n: 4, name: 'สหาย', at: 700, opens: 'มาช่วยเมื่อเจ้าโดนรุม (คนสายยุทธ์) สอนท่าหนึ่งท่า' },
  { n: 5, name: 'ร่วมเป็นร่วมตาย', at: 1200, opens: 'ของขวัญประจำตัวหายาก ท่าล็อกเป้าคู่ ฉายาคู่' },
];

/** The tier of a score (below 0 is still แปลกหน้า: the memory score's standing shows the dislike). */
export function affinityTier(score: number): AffinityTier {
  let t = AFFINITY_TIERS[0]!;
  for (const x of AFFINITY_TIERS) if (score >= x.at) t = x;
  return t;
}

/** What raises and lowers it (points). */
export const AFFINITY = {
  min: -300,
  max: 2000,
  /** A gift a day per person: one they like, one they do not mind, one they dislike. */
  giftLiked: 40,
  giftPlain: 12,
  giftDisliked: -8,
  /** Silver given (npcGift) counts this much per point of memory it gave. */
  goldPerMemory: 2,
  /** Sitting with them at a meal, tea or the fishing (seconds near, at most once a day). */
  togetherSeconds: 15,
  together: 8,
  togetherR: 2.4,
  /** A quest handed in: the one who gave it. */
  quest: 25,
  /** Striking another player before them, killing in their village. */
  strike: -6,
  pkKill: -80,
  /** Family and friends of someone helped in a fight (the one helped gets the event's own points ×1.3). */
  helpedMult: 1.3,
  helpedTies: 5,
  /** The tier-5 gift (a good id, data/professions.ts) given once. */
  bondGift: { good: 'lingzhi', n: 1 },
  /** A sworn friend (tier ≥ 4, the martial) comes to help when this many foes are on the player. */
  rallyFoes: 2,
  rallyR: 26,
} as const;

/** What a person likes and dislikes being given (goods, data/professions.ts), by job; a person may have their own. */
export const JOB_LIKES: Readonly<Record<string, { readonly likes: readonly string[]; readonly dislikes: readonly string[] }>> = {
  farmer: { likes: ['cabbage-seed', 'rice-seed', 'wolf-meat'], dislikes: ['iron-ore'] },
  fisher: { likes: ['cotton', 'charcoal', 'wolf-meat'], dislikes: ['straw'] },
  washer: { likes: ['cotton-cloth', 'indigo-dye', 'silk-thread'], dislikes: ['iron-ore'] },
  vendor: { likes: ['silk-thread', 'cotton-cloth', 'lingzhi'], dislikes: ['straw'] },
  cook: { likes: ['mandarin-fish', 'carp', 'cabbage'], dislikes: ['charcoal'] },
  innkeeper: { likes: ['carp', 'rice', 'cabbage'], dislikes: ['iron-ore'] },
  keeper: { likes: ['herb-leaf', 'lingzhi', 'charcoal'], dislikes: ['wolf-meat'] },
  woodcutter: { likes: ['iron-ingot', 'wolf-meat', 'rice'], dislikes: ['silk-thread'] },
  elder: { likes: ['lingzhi', 'mandarin-fish', 'herb-leaf'], dislikes: ['iron-ore'] },
  musician: { likes: ['silk-thread', 'lingzhi', 'indigo-dye'], dislikes: ['wolf-meat'] },
  child: { likes: ['rice', 'carp', 'cabbage'], dislikes: ['herb-leaf'] },
  disciple: { likes: ['iron-ingot', 'black-iron', 'herb-leaf'], dislikes: ['straw'] },
  teacher: { likes: ['black-iron', 'lingzhi', 'iron-ingot'], dislikes: ['straw'] },
};

/** Said with each tier when reached (the person to the player). */
export const TIER_LINES: Readonly<Record<number, readonly string[]>> = {
  1: ['{name} ใช่ไหม ข้าจำเจ้าได้แล้ว'],
  2: ['เจ้ามาบ่อยนะ {name} ถ้าผ่านมาก็แวะคุยกันได้', 'ข้าแอบบอกนะ ข้าชอบ{like}ที่สุด'],
  3: ['{name} เย็นนี้มากินข้าวด้วยกันไหม', 'เจ้าเหมือนคนในครอบครัวแล้วนะ'],
  4: ['ถ้าใครรังแกเจ้า เรียกข้าได้เลย {name}', 'สหายเอ๋ย มีอะไรให้ช่วยบอกมา'],
  5: ['ชีวิตนี้ข้ายกให้เจ้าครึ่งหนึ่งแล้ว {name} ของนี้ข้าเก็บไว้นาน รับไปเถิด'],
};

/** Said when given a gift: liked, plain, disliked; and the second gift of a day. */
export const GIFT_LINES = {
  liked: ['นี่มันของที่ข้าอยากได้พอดี! ขอบใจมาก', 'เจ้ารู้ใจข้าจริงๆ'],
  plain: ['ขอบใจนะ', 'เกรงใจจัง ขอบใจมาก'],
  disliked: ['เอ่อ… ขอบใจ แต่ข้าไม่ค่อยใช้ของแบบนี้'],
  again: ['วันนี้ให้มาแล้ว เก็บไว้ใช้เองเถอะ'],
} as const;
